import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Copy, Plus, Ruler, Scissors, Shirt, Trash2, Pencil } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { listItems, duplicateItem, deleteItem } from '../api/resources';
import { StatusBadge } from '../components/ui/Badge';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import DataTable from '../components/ui/DataTable';
import PageHeader from '../components/ui/PageHeader';
import { inr, assetUrl } from '../utils/format';

/**
 * Item catalogue. The form itself lives on its own page
 * (/tailoring-items/new and /tailoring-items/:id) rather than in a dialog —
 * it carries rates, tax codes, style types and measurements, which is more
 * than a dialog can hold without a scrollbar of its own.
 */
export default function TailoringItems() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { success, error } = useToast();
  const [del, setDel] = useState(null);

  // DataTable filters/sorts/pages client-side, so fetch the full working set.
  const { data, isLoading } = useQuery({
    queryKey: ['items', 'all'],
    queryFn: () => listItems({ page: 1, perPage: 1000 }),
  });
  const items = data?.tailoringItems || [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['items'] });

  const delMut = useMutation({
    mutationFn: deleteItem,
    onSuccess: (d) => { success(d.message); invalidate(); setDel(null); },
    onError: (e) => { error(e.response?.data?.message || 'Delete failed'); setDel(null); },
  });

  const dupMut = useMutation({
    mutationFn: duplicateItem,
    onSuccess: (d) => { success(d.message); invalidate(); },
    onError: (e) => error(e.response?.data?.message || 'Duplicate failed'),
  });

  const rateLines = (it) => {
    const parts = [];
    if (it.cuttingMasterRate != null) parts.push(['Cutting', it.cuttingMasterRate]);
    if (it.sewingWorkerRate != null) parts.push(['Sewing', it.sewingWorkerRate]);
    if (it.materialExpenseRate != null) parts.push(['Material', it.materialExpenseRate]);
    (it.customWorkRates || []).forEach((cr) => parts.push([cr.name, cr.rateDecimal]));
    return parts;
  };

  const columns = [
    {
      key: 'photo',
      header: '',
      sortable: false,
      width: 'w-14',
      exportValue: () => '',
      render: (it) => (it.photo ? (
        <img src={assetUrl(it.photo)} alt="" className="h-8 w-8 rounded border border-line object-cover" />
      ) : (
        <div className="flex h-8 w-8 items-center justify-center rounded bg-surface-sunken text-content-subtle">
          <Shirt size={15} />
        </div>
      )),
    },
    {
      key: 'name',
      header: 'Item Name',
      hideable: false,
      exportValue: (it) => ((it.subItems || []).length
        ? (it.subItems || []).map((s) => `${it.name} | ${s.name}`).join(', ')
        : it.name),
      // A set is named as it is on an order — "Coat Pant | Pant" — so the
      // catalogue and the order screen read the same way.
      render: (it) => (
        <span className="block">
          <span className="font-medium text-content">{it.name}</span>
          {(it.subItems || []).map((s) => (
            <span key={s.id} className="block text-2xs text-content-muted">
              {it.name} <span className="text-content-subtle">|</span> {s.name}
              <span className="ml-1 tabular-nums text-content-subtle">{inr(s.customerRate)}</span>
            </span>
          ))}
        </span>
      ),
    },
    {
      key: 'customerRate',
      header: 'Customer Rate',
      align: 'right',
      sortValue: (it) => Number(it.customerRate ?? -1),
      exportValue: (it) => (it.customerRate == null ? '' : Number(it.customerRate)),
      render: (it) => (it.customerRate != null
        ? (
          <span className="block">
            <span className="font-semibold">{inr(it.customerRate)}</span>
            {/* A set costs what its parts cost, so say where the number
                came from rather than leaving it looking typed in. */}
            {(it.subItems || []).length > 0 && (
              <span className="block text-2xs font-normal text-content-subtle">
                {it.subItems.length} parts
              </span>
            )}
          </span>
        )
        : <span className="text-content-subtle">—</span>),
    },
    {
      key: 'workRates',
      header: 'Work Rates',
      sortable: false,
      exportValue: (it) => rateLines(it).map(([l, v]) => `${l}: ${v}`).join('; '),
      render: (it) => {
        const parts = rateLines(it);
        if (!parts.length) return <span className="text-content-subtle">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {parts.map(([label, val]) => <span key={label} className="badge-neutral">{label} {inr(val)}</span>)}
          </div>
        );
      },
    },
    {
      key: 'measurements',
      header: 'Measurements',
      sortValue: (it) => (it.measurements?.length || 0),
      exportValue: (it) => (it.measurements || []).map((m) => m.name).join(', '),
      render: (it) => {
        const n = it.measurements?.length || 0;
        if (!n) return <span className="text-2xs text-content-subtle">All</span>;
        return (
          <span
            className="inline-flex items-center gap-1 text-xs text-content-muted"
            title={(it.measurements || []).map((m) => m.name).join(', ')}
          >
            <Ruler size={12} className="text-content-subtle" /> {n}
          </span>
        );
      },
    },
    {
      key: 'taxRate',
      header: 'GST %',
      align: 'right',
      sortValue: (it) => Number(it.taxRate ?? 0),
      render: (it) => `${Number(it.taxRate ?? 0)}%`,
    },
    { key: 'status', header: 'Status', render: (it) => <StatusBadge status={it.status} /> },
    {
      key: 'actions',
      header: '',
      sortable: false,
      hideable: false,
      align: 'right',
      exportValue: () => '',
      render: (it) => (
        <div className="flex items-center justify-end gap-0.5">
          <button className="btn-icon" title="Edit" onClick={() => nav(`/tailoring-items/${it.id}`)}><Pencil size={15} /></button>
          <button className="btn-icon" title="Duplicate" onClick={() => dupMut.mutate(it.id)}><Copy size={15} /></button>
          <button
            className="btn-icon hover:bg-danger-50 hover:text-danger-600"
            title="Delete"
            onClick={() => setDel(it)}
          >
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        icon={Scissors}
        title="Items"
        subtitle="Item catalogue — rates, GST and the measurements each item needs"
        actions={
          <button className="btn-primary" onClick={() => nav('/tailoring-items/new')}>
            <Plus size={15} /> New Item
          </button>
        }
      />

      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        storageKey="tailoring-items"
        searchPlaceholder="Search item name…"
        searchKeys={['name']}
        exportable
        exportFilename="tailoring-items"
        onRowClick={(it) => nav(`/tailoring-items/${it.id}`)}
        emptyTitle="No tailoring items yet"
        emptyHint="Add your first item to start creating orders."
      />

      <ConfirmDialog
        open={!!del}
        onClose={() => setDel(null)}
        title="Delete Item"
        message={`Delete "${del?.name}"? This cannot be undone.`}
        busy={delMut.isPending}
        onConfirm={() => delMut.mutate(del.id)}
      />
    </>
  );
}
